/**
 * PostHog product events. Values follow the `category:action` convention and are lowercase
 * (PostHog actions are case-sensitive). Shared across klayoweblogin (screens) and klayowebapp
 * (post-auth). Add one enum per module/category as tracking is rolled out.
 */
export enum LoginEvent {
  Viewed = "login:viewed",
  ViewedOlc = "login:viewed_olc",
  ViewedOlcBanner = "login:viewed_olc_banner",
  GoSignup = "login:go_signup",
  ClickLoginGoogle = "login:click_login_google",
  ClickLogin = "login:click_login",
  Fail = "login:fail",
  ViewMfa = "login:view_mfa",
  MfaCodeSent = "login:mfa_code_sent",
  MfaCallRequested = "login:mfa_call_requested",
  Success = "login:success",
}

/** Create-account (sign up) module events. Spans the employee / user / org signup apps and the
 * shared signup-flow MFA screen. */
export enum CreateEvent {
  Viewed = "create:viewed",
  ViewedFromInvite = "create:viewed_from_invite",
  ViewedOlcBanner = "create:viewed_olc_banner",
  GoLogin = "create:go_login",
  ClickJoin = "create:click_join",
  EmailVerified = "create:email_verified",
  PhoneCountrySet = "create:phone_country_set",
  PhoneNumberSet = "create:phone_number_set",
  SendCode = "create:send_code",
  CallRequest = "create:call_request",
  PhoneVerified = "create:phone_verified",
}

/**
 * reCAPTCHA outcomes on the sign-in / sign-up screens. Fired from every screen that gates on
 * reCAPTCHA: login, the three create-account submits, and the shared MFA step. The flow is carried
 * as a property rather than encoded in the event name, because the MFA screen serves both flows.
 *
 * This is the ONLY place a reCAPTCHA rejection is recorded against a device: PostHog attaches
 * $browser / $os / $device_type, whereas the API sees no usable client hint (App Insights leaves
 * client_Browser and client_OS empty on those requests).
 */
export enum RecaptchaEvent {
  Failed = "recaptcha:failed",
}

/** Platform-wide events fired once the authenticated user is inside the app (klayowebapp),
 * regardless of route (onboarding or landing). */
export enum PlatformEvent {
  Entered = "platform:entered",
}

/** Onboarding wizard events (klayowebapp) — name / job / organization steps. */
export enum OnboardEvent {
  NameView = "onboard:name_view",
  NameSet = "onboard:name_set",
  JobView = "onboard:job_view",
  JobSearch = "onboard:job_search",
  JobSearchResponse = "onboard:job_search_response",
  JobManual = "onboard:job_manual",
  JobManualSelect = "onboard:job_manual_select",
  JobOverride = "onboard:job_override",
  JobSet = "onboard:job_set",
  OrgView = "onboard:org_view",
  OrgSearch = "onboard:org_search",
  OrgSelect = "onboard:org_select",
  OrgNotListed = "onboard:org_not_listed",
  OrgNotListedSet = "onboard:org_not_listed_set",
  CountrySelected = "onboard:country_selected",
  GetStarted = "onboard:get_started",
  /** Onboarding completed with no steps shown — every field was already supplied (e.g. by LearnUpon
   *  group sync). Distinct from GetStarted so the funnel can tell "skipped" from "abandoned". */
  Skipped = "onboard:skipped",
}

/** Course Catalogue events (klayowebapp). */
export enum CatalogueEvent {
  Viewed = "catalogue:viewed",
  Search = "catalogue:search",
  Filter = "catalogue:filter",
  FilterSet = "catalogue:filter_set",
  FinderClicked = "catalogue:finder_clicked",
  FinderSearch = "catalogue:finder_search",
  FinderResultSelected = "catalogue:finder_result_selected",
  ViewCourse = "catalogue:view_course",
}

/** Course (course detail / purchase / player) events (klayowebapp). Fired when viewing a course
 * regardless of entry point. */
export enum CourseEvent {
  Viewed = "course:viewed",
  ClickBuy = "course:click_buy",
  ClickRequestEnrolment = "course:click_request_enrolment",
  ViewedMultiplePurchaseOptions = "course:viewed_multiple_purchase_options",
  ClickBuyMyself = "course:click_buy_myself",
  ClickBuyTeam = "course:click_buy_team",
  ClickBuyTeamQty = "course:click_buy_team_qty",
  ClickBuyCheckout = "course:click_buy_checkout",
  ClickBuyCancel = "course:click_buy_cancel",
  ClickEnrolStudents = "course:click_enrol_students",
  OpenedContent = "course:opened_content",
  ClickedSimilarCourse = "course:clicked_similar_course",
  ClickStart = "course:click_start",
  ClickResume = "course:click_resume",
  ClickRelaunch = "course:click_relaunch",
  ClickViewCertificate = "course:click_view_certificate",
  LoadedLearnupon = "course:loaded_learnupon",
  ReturnedToKlayo = "course:returned_to_klayo",
}

/** Career detail events (klayowebapp). Every event carries a base payload
 * { jobName, percentMatch, isFullAccess, isCareerGoal, isCurrentRole } plus any extras. */
export enum CareerEvent {
  Viewed = "career:viewed",
  ClickTask = "career:click_task",
  ClickCompetency = "career:click_competency",
  ClickRelatedCourse = "career:click_related_course",
  ClickBenchmark = "career:click_benchmark",
  ClickAddSkills = "career:click_add_skills",
}

/** Job Builder events (klayowebapp) — KI / job-description / scratch job creation flows. */
export enum JobBuilderEvent {
  Viewed = "job_builder:viewed",
  SelectIntelligence = "job_builder:select_intelligence",
  SelectIntelligenceSearch = "job_builder:select_intelligence_search",
  SelectIntelligenceSelectJob = "job_builder:select_intelligence_select_job",
  SelectIntelligenceContinue = "job_builder:select_intelligence_continue",
  SelectIntelligenceToggleTask = "job_builder:select_intelligence_toggle_task",
  SelectIntelligenceToggleCompetency = "job_builder:select_intelligence_toggle_competency",
  SelectIntelligenceImport = "job_builder:select_intelligence_import",
  SelectIntelligenceCancel = "job_builder:select_intelligence_cancel",
  SelectJd = "job_builder:select_jd",
  SelectJdFileUploaded = "job_builder:select_jd_file_uploaded",
  SelectJdContinue = "job_builder:select_jd_continue",
  SelectJdMatchedJobsViewed = "job_builder:select_jd_matched_jobs_viewed",
  SelectJdMatchedJobsSelected = "job_builder:select_jd_matched_jobs_selected",
  SelectJdMatchedJobsImport = "job_builder:select_jd_matched_jobs_import",
  SelectJdTasksCompsViewed = "job_builder:select_jd_tasks_comps_viewed",
  SelectJdTasksCompsToggle = "job_builder:select_jd_tasks_comps_toggle",
  SelectJdImport = "job_builder:select_jd_import",
  SelectJdCancel = "job_builder:select_jd_cancel",
  SelectScratch = "job_builder:select_scratch",
  SelectScratchContinue = "job_builder:select_scratch_continue",
}

/** Job Benchmarking (JobBenchmarkingPage) events (klayowebapp). */
export enum JobBenchmarkingEvent {
  Viewed = "job_benchmarking:viewed",
  OwnJobSelect = "job_benchmarking:own_job_select",
  OwnJobContinue = "job_benchmarking:own_job_continue",
  JdUpload = "job_benchmarking:jd_upload",
  JdContinue = "job_benchmarking:jd_continue",
  BenchmarkViewed = "job_benchmarking:benchmark_viewed",
  TaskToggle = "job_benchmarking:task_toggle",
  CompetencyToggle = "job_benchmarking:competency_toggle",
  Close = "job_benchmarking:close",
  ImportCreate = "job_benchmarking:import_create",
}

/** Manage Training — Courses (ManageTrainingCourses) events (klayowebapp). */
export enum ManageTrainingEvent {
  CoursesViewed = "manage_training:courses_viewed",
  CoursesSearch = "manage_training:courses_search",
  CoursesFilter = "manage_training:courses_filter",
  CoursesFiltered = "manage_training:courses_filtered",
  CoursesFinder = "manage_training:courses_finder",
  CoursesFinderSearch = "manage_training:courses_finder_search",
  CoursesFinderSelect = "manage_training:courses_finder_select",
  ViewedStudents = "manage_training:viewed_students",
  CoursesViewCourse = "manage_training:courses_view_course",
  CoursesToggleView = "manage_training:courses_toggle_view",
  CoursesOrder = "manage_training:courses_order",
  CoursesRecurrence = "manage_training:courses_recurrence",
  StudentsCourseProgressExport = "manage_training:students_course_progress_export",
  StudentsViewed = "manage_training:students_viewed",
  StudentsSearch = "manage_training:students_search",
  StudentsFilter = "manage_training:students_filter",
  StudentsFilterSet = "manage_training:students_filter_set",
  StudentsExport = "manage_training:students_export",
  StudentsCertificatesExport = "manage_training:students_certificates_export",
  StudentsQuickFilter = "manage_training:students_quick_filter",
  StudentsOrder = "manage_training:students_order",
  StudentsCoursesViewed = "manage_training:students_courses_viewed",
  StudentsCoursesViewedHistory = "manage_training:students_courses_viewed_history",
  StudentsEnrol = "manage_training:students_enrol",
  EnrolmentsViewed = "manage_training:enrolments_viewed",
  EnrolmentsQuickFilter = "manage_training:enrolments_quick_filter",
  EnrolmentsSearch = "manage_training:enrolments_search",
  EnrolmentsViewStudentHistory = "manage_training:enrolments_view_student_history",
  EnrolmentsSendReminder = "manage_training:enrolments_send_reminder",
  EnrolmentsEnrolClicked = "manage_training:enrolments_enrol_clicked",
  EnrolViewed = "manage_training:enrol_viewed",
  EnrolCourseAdded = "manage_training:enrol_course_added",
  EnrolStudentAdded = "manage_training:enrol_student_added",
  EnrolCreateStudentClicked = "manage_training:enrol_create_student_clicked",
  EnrolAddToCart = "manage_training:enrol_add_to_cart",
  EnrolAddToCartSuccess = "manage_training:enrol_add_to_cart_success",
  EnrolAddToCartFail = "manage_training:enrol_add_to_cart_fail",
  EnrolEnrolWithBalance = "manage_training:enrol_enrol_with_balance",
  EnrolEnrolSuccess = "manage_training:enrol_enrol_success",
  EnrolEnrolFail = "manage_training:enrol_enrol_fail",
}

/** Benchmarking modal (BenchmarkSummaryDialog) events (klayowebapp). The career-scoped events
 * (Viewed, UnlockedFreeJob) carry `{ role: careerName }` — keep that key so the view → unlock
 * funnel breaks down on one property. UnlockedFreeJob fires only after the unlock succeeds. */
export enum BenchmarkEvent {
  Viewed = "benchmark:viewed",
  ToggleCompetencies = "benchmark:toggle_competencies",
  ToggleTraining = "benchmark:toggle_training",
  ToggleMatched = "benchmark:toggle_matched",
  ToggleCompleted = "benchmark:toggle_completed",
  SelectCompetency = "benchmark:select_competency",
  AddGap = "benchmark:add_gap",
  ClickTraining = "benchmark:click_training",
  UnlockedFreeJob = "benchmark:unlocked_free_job",
  Closed = "benchmark:closed",
}

/** Career Path (CareerPlanningDialog / learning pathway) events (klayowebapp). */
export enum CareerPathEvent {
  Viewed = "careerpath:viewed",
  OpenTraining = "careerpath:open_training",
  GotoTraining = "careerpath:goto_training",
}

/** Role & career selector (CareerPathDialog) events (klayowebapp). */
export enum RoleEvent {
  Viewed = "role:viewed",
  Selected = "role:selected",
  GoalViewed = "role:goal_viewed",
  GoalSelected = "role:goal_selected",
}

/** Skills Passport events (klayowebapp). skill_auto_added and click_benchmark are intentionally
 * not defined here: auto-added skills are emitted server-side, and there is no benchmark entry
 * point on this page yet. */
export enum PassportEvent {
  Viewed = "passport:viewed",
  ClickPublicProfile = "passport:click_public_profile",
  ClickAddSkill = "passport:click_add_skill",
  ClickRemoveSkill = "passport:click_remove_skill",
  Search = "passport:search",
  FilterCategory = "passport:filter_category",
  SkillShareLinkedin = "passport:skill_share_linkedin",
  SkillShareEmail = "passport:skill_share_email",
  SkillShareFacebook = "passport:skill_share_facebook",
  OpenSkill = "passport:open_skill",
  AddSkill = "passport:add_skill",
  EditSkill = "passport:edit_skill",
}

/** My Profile — My Job section (ProfileJobSection) events (klayowebapp). */
export enum ProfileEvent {
  JobViewed = "profile:job_viewed",
  JobCompetencyClick = "profile:job_competency_click",
  JobTasksToggled = "profile:job_tasks_toggled",
  ViewDetails = "profile:view_details",
  CompetenciesViewed = "profile:competencies_viewed",
  CompetenciesRowClicked = "profile:competencies_row_clicked",
  CompetenciesMenuClicked = "profile:competencies_menu_clicked",
  CompetenciesSorted = "profile:competencies_sorted",
  CompetenciesEdit = "profile:competencies_edit",
  CompetenciesRemove = "profile:competencies_remove",
  CompetenciesAddButton = "profile:competencies_add_button",
  CompetenciesAddSearch = "profile:competencies_add_search",
  CompetenciesAdded = "profile:competencies_added",
  CompetenciesSearched = "profile:competencies_searched",
  CompetenciesFiltered = "profile:competencies_filtered",
  GapsViewed = "profile:gaps_viewed",
  GapsMenuClicked = "profile:gaps_menu_clicked",
  GapsMenuAdd = "profile:gaps_menu_add",
  GapsSearched = "profile:gaps_searched",
  GapsFiltered = "profile:gaps_filtered",
  GapsSorted = "profile:gaps_sorted",
  TrainingViewed = "profile:training_viewed",
  TrainingSearched = "profile:training_searched",
  TrainingAdd = "profile:training_add",
  TrainingClicked = "profile:training_clicked",
  JobMatchingViewed = "profile:jobmatching_viewed",
  JobMatchingMinimumToggled = "profile:jobmatching_minimum_toggled",
  JobMatchingSearched = "profile:jobmatching_searched",
  JobMatchingFilterOpened = "profile:jobmatching_filter_opened",
  JobMatchingFilterSet = "profile:jobmatching_filter_set",
  JobMatchingSorted = "profile:jobmatching_sorted",
  JobMatchingRowClick = "profile:jobmatching_row_click",
  JobMatchingPrint = "profile:jobmatching_print",
}

/** My Team — People section (TeamPeopleSection) events (klayowebapp). Group mode uses
 * GroupEvent with the same actions plus a groupName param. */
export enum MyTeamEvent {
  PeopleViewed = "myteam:people_viewed",
  Sorted = "myteam:sorted",
  Exported = "myteam:exported",
  ViewToggled = "myteam:view_toggled",
  ProfileOpened = "myteam:profile_opened",
  RegsViewed = "myteam:regs_viewed",
  RegsBubbleClick = "myteam:regs_bubble_click",
  RegsGapsToggle = "myteam:regs_gaps_toggle",
  RegsRowTotalClicked = "myteam:regs_row_total_clicked",
  RegsRowHaveClicked = "myteam:regs_row_have_clicked",
  RegsRowGapsClicked = "myteam:regs_row_gaps_clicked",
  RegsRowDetailOpened = "myteam:regs_row_detail_opened",
  RegsRowDetailSubOpened = "myteam:regs_row_detail_sub_opened",
  RegsSearched = "myteam:regs_searched",
  TasksViewed = "myteam:tasks_viewed",
  TasksBubbleClick = "myteam:tasks_bubble_click",
  TasksGapsToggle = "myteam:tasks_gaps_toggle",
  TasksRowTotalClicked = "myteam:tasks_row_total_clicked",
  TasksRowHaveClicked = "myteam:tasks_row_have_clicked",
  TasksRowGapsClicked = "myteam:tasks_row_gaps_clicked",
  TasksRowDetailToggled = "myteam:tasks_row_detail_toggled",
  TasksSearched = "myteam:tasks_searched",
  CompetenciesViewed = "myteam:competencies_viewed",
  CompetencyBubbleClick = "myteam:competency_bubble_click",
  CompetencyGapsToggle = "myteam:competency_gaps_toggle",
  CompetencyRowTotalClicked = "myteam:competency_row_total_clicked",
  CompetencyRowHaveClicked = "myteam:competency_row_have_clicked",
  CompetencyRowGapsClicked = "myteam:competency_row_gaps_clicked",
  TrainingViewed = "myteam:training_viewed",
  TrainingBubbleClick = "myteam:training_bubble_click",
  TrainingGapsToggle = "myteam:training_gaps_toggle",
  TrainingRowTotalClicked = "myteam:training_row_total_clicked",
  TrainingRowHaveClicked = "myteam:training_row_have_clicked",
  TrainingRowGapsClicked = "myteam:training_row_gaps_clicked",
  TrainingSearched = "myteam:training_searched",
  FiltersOpened = "myteam:filters_opened",
  FiltersClosed = "myteam:filters_closed",
  FiltersApplied = "myteam:filters_applied",
  FilterChanged = "myteam:filter_changed",
}

/** Group (Group view) counterpart of MyTeamEvent — same actions, group: prefix, groupName param. */
export enum GroupEvent {
  PeopleViewed = "group:people_viewed",
  Sorted = "group:sorted",
  Exported = "group:exported",
  ViewToggled = "group:view_toggled",
  ProfileOpened = "group:profile_opened",
  RegsViewed = "group:regs_viewed",
  RegsBubbleClick = "group:regs_bubble_click",
  RegsGapsToggle = "group:regs_gaps_toggle",
  RegsRowTotalClicked = "group:regs_row_total_clicked",
  RegsRowHaveClicked = "group:regs_row_have_clicked",
  RegsRowGapsClicked = "group:regs_row_gaps_clicked",
  RegsRowDetailOpened = "group:regs_row_detail_opened",
  RegsRowDetailSubOpened = "group:regs_row_detail_sub_opened",
  RegsSearched = "group:regs_searched",
  TasksViewed = "group:tasks_viewed",
  TasksBubbleClick = "group:tasks_bubble_click",
  TasksGapsToggle = "group:tasks_gaps_toggle",
  TasksRowTotalClicked = "group:tasks_row_total_clicked",
  TasksRowHaveClicked = "group:tasks_row_have_clicked",
  TasksRowGapsClicked = "group:tasks_row_gaps_clicked",
  TasksRowDetailToggled = "group:tasks_row_detail_toggled",
  TasksSearched = "group:tasks_searched",
  CompetenciesViewed = "group:competencies_viewed",
  CompetencyBubbleClick = "group:competency_bubble_click",
  CompetencyGapsToggle = "group:competency_gaps_toggle",
  CompetencyRowTotalClicked = "group:competency_row_total_clicked",
  CompetencyRowHaveClicked = "group:competency_row_have_clicked",
  CompetencyRowGapsClicked = "group:competency_row_gaps_clicked",
  TrainingViewed = "group:training_viewed",
  TrainingBubbleClick = "group:training_bubble_click",
  TrainingGapsToggle = "group:training_gaps_toggle",
  TrainingRowTotalClicked = "group:training_row_total_clicked",
  TrainingRowHaveClicked = "group:training_row_have_clicked",
  TrainingRowGapsClicked = "group:training_row_gaps_clicked",
  TrainingSearched = "group:training_searched",
  FiltersOpened = "group:filters_opened",
  FiltersClosed = "group:filters_closed",
  FiltersApplied = "group:filters_applied",
  FilterChanged = "group:filter_changed",
}

/** Careers list events (klayowebapp). */
export enum CareersEvent {
  ViewedList = "careers:viewed_list",
  Filtered = "careers:filtered",
  Sorted = "careers:sorted",
  ClickedCareer = "careers:clicked_career",
}

/** My Courses (My Learning) events (klayowebapp). */
export enum MyCoursesEvent {
  Viewed = "mycourses:viewed",
  Filter = "mycourses:filter",
  ClickViewCourse = "mycourses:click_view_course",
  ClickResume = "mycourses:click_resume",
  ClickViewCertificate = "mycourses:click_view_certificate",
  ClickRelaunch = "mycourses:click_relaunch",
  ClickStart = "mycourses:click_start",
}

/** Pricing cart (PricingPage — Careers & Workforce tabs) events (klayowebapp). */
export enum CartEvent {
  CareersViewed = "cart:careers_viewed",
  CareersToggleWhatYouGet = "cart:careers_toggle_what_you_get",
  CareersSearch = "cart:careers_search",
  CareersFilter = "cart:careers_filter",
  CareersAddToCart = "cart:careers_add_to_cart",
  CareersRemoveFromCart = "cart:careers_remove_from_cart",
  CareersContinue = "cart:careers_continue",
  WorkforceViewed = "cart:workforce_viewed",
  WorkforceViewDetail = "cart:workforce_view_detail",
  WorkforceAddToCart = "cart:workforce_add_to_cart",
  WorkforceRemoveFromCart = "cart:workforce_remove_from_cart",
  WorkforceContinue = "cart:workforce_continue",
}

/** Checkout (PricingPage CheckoutView) events (klayowebapp). */
export enum CheckoutEvent {
  Loaded = "checkout:loaded",
  BillingLoaded = "checkout:billing_loaded",
  BillingContinue = "checkout:billing_continue",
  BillingMethodSelected = "checkout:billing_method_selected",
  BillingRequestInvoice = "checkout:billing_request_invoice",
  BillingRequestQuote = "checkout:billing_request_quote",
  BillingRequestCard = "checkout:billing_request_card",
  PaymentCompleted = "checkout:payment_completed",
  PaymentPending = "checkout:payment_pending",
  Closed = "checkout:closed",
}

/** Practical create/edit detail (AddEditPracticalView) events (klayowebapp). */
export enum PracticalDetailEvent {
  Opened = "practical_detail:opened",
  NameSet = "practical_detail:name_set",
  DescriptionSet = "practical_detail:description_set",
  AddedAttachment = "practical_detail:added_attachment",
  TypeSet = "practical_detail:type_set",
  InstructorSet = "practical_detail:instructor_set",
  LocationSet = "practical_detail:location_set",
  WeatherSet = "practical_detail:weather_set",
  WeatherRetrieved = "practical_detail:weather_retrieved",
  DurationSet = "practical_detail:duration_set",
  AssessmentToggled = "practical_detail:assessment_toggled",
  CompetencyAdded = "practical_detail:competency_added",
  CompetencyRemoved = "practical_detail:competency_removed",
  Saved = "practical_detail:saved",
  ChangeLogViewed = "practical_detail:change_log_viewed",
}

/** Practical participants management (AddEditPracticalView + participant dialogs) events. */
export enum PracticalParticipantsEvent {
  Viewed = "practical_participants:viewed",
  Add = "practical_participants:add",
  Added = "practical_participants:added",
  Removed = "practical_participants:removed",
  AddParticipantOpened = "practical_participants:add_participant_opened",
  AddParticipantAdded = "practical_participants:add_participant_added",
  ParticipantConfigOpened = "practical_participants:participant_config_opened",
  ParticipantConfigAttachmentAdded = "practical_participants:participant_config_attachment_added",
  ParticipantConfigSaved = "practical_participants:participant_config_saved",
}

/** Practicals (instructor Practicals list) events (klayowebapp). */
export enum PracticalsEvent {
  Viewed = "practicals:viewed",
  Sorted = "practicals:sorted",
  Searched = "practicals:searched",
  MenuOptionView = "practicals:menu_option_view",
  MenuOptionDuplicate = "practicals:menu_option_duplicate",
  MenuOptionDelete = "practicals:menu_option_delete",
  MenuOptionSend = "practicals:menu_option_send",
  MenuOptionSchedule = "practicals:menu_option_schedule",
  RowClick = "practicals:row_click",
}

/** Manage — Regulations (SettingsRegulationsView) events (klayowebapp). */
export enum ManageRegsEvent {
  Viewed = "manage_regs:viewed",
  NewRegulation = "manage_regs:new_regulation",
  NewDomain = "manage_regs:new_domain",
  NewCategory = "manage_regs:new_category",
  MenuOption = "manage_regs:menu_option",
  Search = "manage_regs:search",
}

/** Manage — Company (SettingsCompanyView) events (klayowebapp). */
export enum ManageCompanyEvent {
  Viewed = "manage_company:viewed",
  AddAddress = "manage_company:add_address",
  LocationAdd = "manage_company:location_add",
  LocationMenuOption = "manage_company:location_menu_option",
  ProficiencyInfo = "manage_company:proficiency_info",
  JobMatchingToggle = "manage_company:job_matching_toggle",
  TrainingProviderAdded = "manage_company:training_provider_added",
  TrainingProviderMenuOption = "manage_company:training_provider_menu_option",
}

/** Manage — Competencies (SettingsAttributesView + intelligence import) events (klayowebapp). */
export enum ManageCompetenciesEvent {
  Viewed = "manage_competencies:viewed",
  Searched = "manage_competencies:searched",
  FilterSet = "manage_competencies:filter_set",
  NewCompetency = "manage_competencies:new_competency",
  NewCompetencyEmpty = "manage_competencies:new_competency_empty",
  NewCompetencyIntelligence = "manage_competencies:new_competency_intelligence",
  NewCompetencyCreate = "manage_competencies:new_competency_create",
  NewCompetencySaveAndCreate = "manage_competencies:new_competency_save_and_create",
  NewCompetencyIntelligenceSearch = "manage_competencies:new_competency_intelligence_search",
  NewCompetencyIntelligenceImport = "manage_competencies:new_competency_intelligence_import",
  JobsClicked = "manage_competencies:jobs_clicked",
  RowClicked = "manage_competencies:row_clicked",
  MenuOption = "manage_competencies:menu_option",
  CategoriesViewed = "manage_competencies:categories_viewed",
  CategoriesNew = "manage_competencies:categories_new",
  CategoriesMenuOption = "manage_competencies:categories_menu_option",
}

/** Manage — Checklists (SettingsChecklistsView) events (klayowebapp). */
export enum ManageChecklistsEvent {
  Viewed = "manage_checklists:viewed",
  NewChecklist = "manage_checklists:new_checklist",
  RowClicked = "manage_checklists:row_clicked",
  MenuOption = "manage_checklists:menu_option",
  Search = "manage_checklists:search",
}

/** Manage — Groups (SettingsGroupsView) events (klayowebapp). */
export enum ManageGroupsEvent {
  Viewed = "manage_groups:viewed",
  NewGroup = "manage_groups:new_group",
  MenuOption = "manage_groups:menu_option",
  RowClicked = "manage_groups:row_clicked",
}

/** Manage — Tasks (SettingsTasksView + intelligence import) events (klayowebapp). */
export enum ManageTasksEvent {
  Viewed = "manage_tasks:viewed",
  NewTaskBase = "manage_tasks:new_task_base",
  NewTaskEmpty = "manage_tasks:new_task_empty",
  NewTaskEmptySearch = "manage_tasks:new_task_empty_search",
  NewTaskEmptySelectSuggestion = "manage_tasks:new_task_empty_select_suggestion",
  NewTaskImport = "manage_tasks:new_task_import",
  NewTaskIntelligence = "manage_tasks:new_task_intelligence",
  NewTaskIntelligenceSearch = "manage_tasks:new_task_intelligence_search",
  NewTaskIntelligenceSelect = "manage_tasks:new_task_intelligence_select",
  NewTaskIntelligenceViewDetail = "manage_tasks:new_task_intelligence_view_detail",
  NewTaskIntelligenceImport = "manage_tasks:new_task_intelligence_import",
  TaskRowMenuOption = "manage_tasks:task_row_menu_option",
}

/** Manage — Jobs (SettingsJobsView + SettingsJobBuilderView) events (klayowebapp). */
export enum ManageJobsEvent {
  Viewed = "manage_jobs:viewed",
  Searched = "manage_jobs:searched",
  Benchmark = "manage_jobs:benchmark",
  NewJob = "manage_jobs:new_job",
  NewJobFromIntelligence = "manage_jobs:new_job_from_intelligence",
  NewJobFromJd = "manage_jobs:new_job_from_jd",
  NewJobFromScratch = "manage_jobs:new_job_from_scratch",
  ExpandJob = "manage_jobs:expand_job",
  MenuOption = "manage_jobs:menu_option",
}

/** Manage — Employees (SettingsEmployeesView) events (klayowebapp). */
export enum ManageEmployeesEvent {
  Viewed = "manage_employees:viewed",
  NewEmployeeLimited = "manage_employees:new_employee_limited",
  NewEmployeeFull = "manage_employees:new_employee_full",
  Search = "manage_employees:search",
  FilterSet = "manage_employees:filter_set",
  FullAccessInfo = "manage_employees:full_access_info",
  EmployeeRowClick = "manage_employees:employee_row_click",
  EmployeeSelected = "manage_employees:employee_selected",
  EmployeeSelectionAction = "manage_employees:employee_selection_action",
  MenuOption = "manage_employees:menu_option",
  EmployeeAdded = "manage_employees:employee_added",
}

/** My Requests — Competencies feedback (Feedback view) events (klayowebapp). */
export enum MyRequestsEvent {
  CompetenciesViewed = "myrequests:competencies_viewed",
  CompetenciesSearched = "myrequests:competencies_searched",
  CompetenciesFiltered = "myrequests:competencies_filtered",
  CompetenciesGroupChanged = "myrequests:competencies_group_changed",
  CompetenciesViewedDetail = "myrequests:competencies_viewed_detail",
  CompetenciesFeedbackOpen = "myrequests:competencies_feedback_open",
  CompetenciesFeedbackClose = "myrequests:competencies_feedback_close",
  CompetenciesFeedbackRequestChanges = "myrequests:competencies_feedback_requestchanges",
  CompetenciesFeedbackAccept = "myrequests:competencies_feedback_accept",
  LearningViewed = "myrequests:learning_viewed",
  LearningSearched = "myrequests:learning_searched",
  LearningFiltered = "myrequests:learning_filtered",
  LearningRequestViewed = "myrequests:learning_request_viewed",
  LearningRequestDeclined = "myrequests:learning_request_declined",
  LearningRequestAccepted = "myrequests:learning_request_accepted",
}

/** Solution Explorer / Finder (SolutionFinderPage) events (klayowebapp). */
export enum SolutionsEvent {
  Opened = "solutions:opened",
  Closed = "solutions:closed",
  FindViewedProblems = "solutions:find_viewed_problems",
  FindSearchProblems = "solutions:find_search_problems",
  FindClickProblem = "solutions:find_click_problem",
  FindTrySolution = "solutions:find_try_solution",
  FindViewPricing = "solutions:find_view_pricing",
  FindContactClicked = "solutions:find_contact_clicked",
  BrowseViewed = "solutions:browse_viewed",
  BrowseTrySolution = "solutions:browse_try_solution",
  BrowseViewPricing = "solutions:browse_view_pricing",
}

/**
 * "Try It Now" preview-sandbox (demo data) events (klayowebapp). Nothing that happens *during* a
 * demo is reported — only its two edges, captured either side of the sandbox.
 *
 * The two do NOT reconcile 1:1, so don't build a funnel expecting them to: `Entered` counts
 * launches, `Viewed` counts distinct solutions per return trip (re-entering the same demo twice
 * before coming back yields two of the first and one of the second).
 */
export enum DemoDataEvent {
  /** A demo was launched. `{ solutionCode, source }` — see `DemoSource` for the entry points. */
  Entered = "demo_data:entered",
  /** A demo was run and the prospect came back to the real app. `{ solutionCode }`, no source. */
  Viewed = "demo_data:viewed",
}

/**
 * KIM assistant events (klayowebapp). Code identifiers for the feature are all `Kit*`; KIM is the
 * product name, so the analyst-facing prefix uses it. Answer text is never sent.
 */
export enum KimEvent {
  /** `{ length, questionNumber, question }` — `question` is the text truncated to 256 chars. */
  Question = "kim:question",
  /** A reply rendered. NOT a success rate: KitService also 200s shield refusals and model errors. */
  Answered = "kim:answered",
  /** Transport failure only — see the caveat on Answered. */
  Failed = "kim:failed",
}
